import React, { useState, useEffect } from 'react';
import { View, Text, StyleSheet, ScrollView, TouchableOpacity, Modal, Alert, Platform } from 'react-native';
import { useRouter } from 'expo-router';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { LinearGradient } from 'expo-linear-gradient';
import { Ionicons } from '@expo/vector-icons';
import { Button } from '@/components/ui/button';
import { Brand, Neutrals } from '@/constants/theme';
import { useAuth } from '@/hooks/use-auth';
import { getFunctionsInvokeErrorMessage } from '@/lib/functions-error';
import { supabase } from '@/lib/supabase';

export default function DeleteAccountScreen() {
  const router = useRouter();
  const insets = useSafeAreaInsets();
  const { user, loading: authLoading, signOut } = useAuth();
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      router.replace('/(auth)/sign-in');
    }
  }, [authLoading, user, router]);

  const confirmDelete = async () => {
    if (!supabase) {
      Alert.alert('Error', 'Supabase is not configured.');
      return;
    }
    setDeleteLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('delete-account', { body: {} });
      const payload = data as { ok?: boolean; error?: string } | null;
      if (error || payload?.error) {
        const message = await getFunctionsInvokeErrorMessage(error, payload);
        Alert.alert('Error', message);
        return;
      }
      setShowConfirmModal(false);
      await signOut();
      router.replace('/(auth)/sign-in');
    } catch (e) {
      Alert.alert('Error', e instanceof Error ? e.message : 'Failed to delete account. Please try again.');
    } finally {
      setDeleteLoading(false);
    }
  };

  if (authLoading || !user) {
    return null;
  }

  return (
    <View style={styles.container}>
      <LinearGradient
        colors={[...Brand.primaryGradient]}
        style={styles.background}
      />
      <ScrollView
        contentContainerStyle={[styles.scrollContent, { paddingTop: Math.max(insets.top, 16) }]}
        keyboardShouldPersistTaps="handled"
      >
        <TouchableOpacity
          style={styles.backButton}
          onPress={() => router.back()}
          accessibilityLabel="Go back"
        >
          <Ionicons name="arrow-back" size={20} color={Brand.primary} />
          <Text style={styles.backText}>Back</Text>
        </TouchableOpacity>

        <View style={styles.header}>
          <View style={styles.iconContainer}>
            <Ionicons name="trash-outline" size={48} color={Neutrals.destructiveMuted} />
          </View>
          <Text style={styles.title}>Delete account</Text>
          <Text style={styles.subtitle}>
            Permanently remove your account, profile, recipes, and all associated data. This action cannot be undone.
          </Text>
        </View>

        <View style={styles.card}>
          <Button
            testID="delete-account-open-modal"
            variant="destructive"
            onPress={() => setShowConfirmModal(true)}
            disabled={deleteLoading}
            style={styles.deleteButton}
          >
            Delete my account
          </Button>
        </View>
      </ScrollView>

      <Modal
        visible={showConfirmModal}
        transparent
        animationType="fade"
        onRequestClose={() => !deleteLoading && setShowConfirmModal(false)}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <Text style={styles.modalTitle}>Delete account</Text>
            <Text style={styles.modalDescription}>
              Are you sure you want to delete your account? This will permanently remove your profile, recipes, and all data. This action cannot be undone.
            </Text>
            <View style={styles.modalActions}>
              <Button
                testID="delete-account-cancel"
                variant="outline"
                onPress={() => setShowConfirmModal(false)}
                style={styles.modalButton}
                disabled={deleteLoading}
              >
                Cancel
              </Button>
              <Button
                testID="delete-account-confirm"
                variant="destructive"
                onPress={confirmDelete}
                style={styles.modalButton}
                disabled={deleteLoading}
              >
                {deleteLoading ? 'Deleting…' : 'Delete account'}
              </Button>
            </View>
          </View>
        </View>
      </Modal>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  background: {
    position: 'absolute',
    left: 0,
    right: 0,
    top: 0,
    bottom: 0,
  },
  scrollContent: {
    padding: 16,
    paddingBottom: 100,
    maxWidth: 864,
    alignSelf: 'center',
    width: '100%',
  },
  backButton: {
    flexDirection: 'row',
    alignItems: 'center',
    alignSelf: 'flex-start',
    gap: 6,
    marginBottom: 24,
    paddingVertical: 4,
  },
  backText: {
    color: Brand.primary,
    fontSize: 16,
    fontWeight: '600',
  },
  header: {
    marginBottom: 24,
  },
  iconContainer: {
    marginBottom: 16,
  },
  title: {
    fontSize: 28,
    fontWeight: 'bold',
    color: Neutrals.textPrimary,
    marginBottom: 8,
  },
  subtitle: {
    fontSize: 16,
    color: Neutrals.textSecondary,
    lineHeight: 22,
  },
  card: {
    backgroundColor: '#fff',
    borderRadius: 16,
    padding: 24,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.08,
    shadowRadius: 8,
    elevation: Platform.OS === 'android' ? 0 : 4,
  },
  deleteButton: {
    width: '100%',
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: Neutrals.overlay,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  modalContent: {
    backgroundColor: '#fff',
    borderRadius: 24,
    padding: 24,
    width: '100%',
    maxWidth: 400,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.3,
    shadowRadius: 8,
    elevation: Platform.OS === 'android' ? 0 : 10,
  },
  modalTitle: {
    fontSize: 24,
    fontWeight: 'bold',
    color: Neutrals.textPrimary,
    marginBottom: 12,
    textAlign: 'center',
  },
  modalDescription: {
    fontSize: 16,
    color: Neutrals.textSecondary,
    marginBottom: 24,
    textAlign: 'center',
    lineHeight: 22,
  },
  modalActions: {
    flexDirection: 'row',
    gap: 12,
  },
  modalButton: {
    flex: 1,
  },
});
